import axios from 'axios';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getGeonorgeToken } from '../../api/urls';
import { GeonorgeContext } from './GeonorgeContext';

const GeonorgeProvider = ({ children }) => {
  const [token, setToken] = useState('');
  const [expirationTime, setExpirationTime] = useState('');
  const [initialized, setInitialized] = useState(false);
  const refreshTimerRef = useRef(null);

  const hentToken = useCallback(async () => {
    try {
      const response = await axios.get(getGeonorgeToken(), {
        headers: { 'X-Bymelding-Client': 'EntreprenorkontrollClient' },
      });
      const { data } = response;
      setToken(data.result.token);
      setExpirationTime(data.result.expiry);

      // Backend returnerer expiry i UTC uten timezone-indikator ('Z').
      // Nettleseren tolker da strengen som lokal tid (CEST = UTC+2), noe som
      // gjør at tokenet ser ut til å ha løpt ut før det ble generert.
      // Vi legger til 'Z' for å tvinge korrekt UTC-parsing.
      const expiryString =
        data.result.expiry.endsWith('Z') ||
        /[+-]\d{2}:\d{2}$/.test(data.result.expiry)
          ? data.result.expiry
          : `${data.result.expiry}Z`;
      const expiryDate = new Date(expiryString);
      // Automatisk refresh av token 5 minutter før utløp
      const now = new Date();
      const timeUntilExpiry = expiryDate.getTime() - now.getTime();
      const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 0);

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      if (refreshTime > 0) {
        const timer = setTimeout(() => {
          hentToken();
        }, refreshTime);
        refreshTimerRef.current = timer;
      }
    } catch (error) {
      console.error('Feil ved henting av Geonorge token:', error);
      setToken('');
      setExpirationTime('');
    } finally {
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    hentToken();
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [hentToken]);

  const contextValue = useMemo(
    () => ({
      token,
      expirationTime,
      initialized,
    }),
    [token, expirationTime, initialized]
  );

  return (
    <GeonorgeContext.Provider value={contextValue}>
      {children}
    </GeonorgeContext.Provider>
  );
};

export default GeonorgeProvider;
